import * as signalR from '@microsoft/signalr'

const SIGNALR_URL = import.meta.env.VITE_SIGNALR_URL || 'https://coms.hadv.quest'
const FLOW_SIGNALR_URL = import.meta.env.VITE_FLOW_SIGNALR_URL || 'https://signalr.hadv.io'

class SignalRService {
  private chatConnection: signalR.HubConnection | null = null
  private chatConnectionPromise: Promise<signalR.HubConnection> | null = null
  private notificationConnection: signalR.HubConnection | null = null
  private flowConnection: signalR.HubConnection | null = null

  // Chat Hub
  async startChatConnection(token: string) {
    if (this.chatConnection?.state === signalR.HubConnectionState.Connected) {
      return this.chatConnection
    }

    // Return existing promise if connection is being established (prevents double connections)
    if (this.chatConnectionPromise) {
      return this.chatConnectionPromise
    }

    // Use Trace for most detailed SignalR logs, Debug for detailed, Information for normal
    const logLevel = import.meta.env.DEV ? signalR.LogLevel.Trace : signalR.LogLevel.Information

    this.chatConnectionPromise = (async () => {
      this.chatConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${SIGNALR_URL}/hub/chat`, {
          accessTokenFactory: () => token,
        })
        .withAutomaticReconnect()
        .configureLogging(logLevel)
        .build()

      // Add connection state handlers for chat
      this.chatConnection.onreconnecting(error => {
        console.warn('⚠️ SignalR Chat reconnecting...', error)
      })

      this.chatConnection.onreconnected(connectionId => {
        console.log('✅ SignalR Chat reconnected. Connection ID:', connectionId)
      })

      this.chatConnection.onclose(error => {
        console.error('❌ SignalR Chat connection closed:', error)
      })

      await this.chatConnection.start()
      console.log('SignalR Chat Connected')
      if (this.isDebugEnabled()) this.attachDebugInterceptors()
      return this.chatConnection
    })()

    try {
      return await this.chatConnectionPromise
    } catch (err) {
      console.error('SignalR Chat Connection Error:', err)
      throw err
    } finally {
      this.chatConnectionPromise = null
    }
  }

  async stopChatConnection() {
    if (this.chatConnection) {
      await this.chatConnection.stop()
      this.chatConnection = null
      console.log('SignalR Chat Disconnected')
    }
  }

  getChatConnection() {
    return this.chatConnection
  }

  // Notification Hub
  async startNotificationConnection(token: string) {
    if (this.notificationConnection?.state === signalR.HubConnectionState.Connected) {
      console.log('✅ SignalR Notifications already connected')
      return this.notificationConnection
    }

    console.log('🔌 Attempting to connect to Notification Hub...')
    console.log('📍 Hub URL:', `${SIGNALR_URL}/hub/notifications`)

    this.notificationConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hub/notifications`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.random() * 10000
          } else {
            return null
          }
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()

    // Add connection state handlers
    this.notificationConnection.onreconnecting(error => {
      console.warn('⚠️ SignalR Notifications reconnecting...', error)
    })

    this.notificationConnection.onreconnected(connectionId => {
      console.log('✅ SignalR Notifications reconnected. Connection ID:', connectionId)
    })

    this.notificationConnection.onclose(error => {
      console.error('❌ SignalR Notifications connection closed:', error)
    })

    try {
      await this.notificationConnection.start()
      console.log('✅ SignalR Notifications Connected')
      console.log('📡 Connection ID:', this.notificationConnection.connectionId)
      console.log('🔊 Connection State:', this.notificationConnection.state)
      if (this.isDebugEnabled()) this.attachDebugInterceptors()
      return this.notificationConnection
    } catch (err) {
      console.error('❌ SignalR Notifications Connection Error:', err)
      throw err
    }
  }

  async stopNotificationConnection() {
    if (this.notificationConnection) {
      console.log('⚠️ Stopping notification connection...')
      await this.notificationConnection.stop()
      this.notificationConnection = null
      console.log('🔌 SignalR Notifications Disconnected')
    } else {
      console.log('ℹ️ No notification connection to stop')
    }
  }

  getNotificationConnection() {
    return this.notificationConnection
  }

  // Flow Hub (real-time collaboration)
  async startFlowConnection(token: string) {
    if (this.flowConnection?.state === signalR.HubConnectionState.Connected) {
      console.log('SignalR Flow already connected')
      return this.flowConnection
    }

    console.log('Connecting to Flow Hub...')
    console.log('Hub URL:', `${FLOW_SIGNALR_URL}/hub/flow`)

    const logLevel = import.meta.env.DEV ? signalR.LogLevel.Debug : signalR.LogLevel.Information

    this.flowConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${FLOW_SIGNALR_URL}/hub/flow`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          // Exponential backoff with jitter, max 30 seconds
          if (retryContext.elapsedMilliseconds < 60000) {
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000)
            return delay + Math.random() * 1000
          }
          return null
        }
      })
      .configureLogging(logLevel)
      .build()

    // Add connection state handlers
    this.flowConnection.onreconnecting(error => {
      console.warn('SignalR Flow reconnecting...', error)
    })

    this.flowConnection.onreconnected(connectionId => {
      console.log('SignalR Flow reconnected. Connection ID:', connectionId)
    })

    this.flowConnection.onclose(error => {
      console.error('SignalR Flow connection closed:', error)
    })

    try {
      await this.flowConnection.start()
      console.log('SignalR Flow Connected')
      console.log('Connection ID:', this.flowConnection.connectionId)
      if (this.isDebugEnabled()) this.attachDebugInterceptors()
      return this.flowConnection
    } catch (err) {
      console.error('SignalR Flow Connection Error:', err)
      throw err
    }
  }

  async stopFlowConnection() {
    if (this.flowConnection) {
      console.log('Stopping flow connection...')
      await this.flowConnection.stop()
      this.flowConnection = null
      console.log('SignalR Flow Disconnected')
    }
  }

  getFlowConnection() {
    return this.flowConnection
  }

  // Disconnect all
  async disconnectAll() {
    await this.stopChatConnection()
    await this.stopNotificationConnection()
    await this.stopFlowConnection()
  }

  // ========================
  // Debug Toggle
  // ========================
  private debugCleanups: Array<() => void> = []

  isDebugEnabled(): boolean {
    return localStorage.getItem('signalr-debug') === 'true'
  }

  enableDebug() {
    localStorage.setItem('signalr-debug', 'true')
    this.attachDebugInterceptors()
    console.log(
      '%c[SignalR Debug] ENABLED — all hub events will be logged. Run signalRDebug(false) to disable.',
      'color: #4caf50; font-weight: bold'
    )
  }

  disableDebug() {
    localStorage.removeItem('signalr-debug')
    this.detachDebugInterceptors()
    console.log(
      '%c[SignalR Debug] DISABLED',
      'color: #f44336; font-weight: bold'
    )
  }

  /** Patch active connections to log every server→client invocation */
  attachDebugInterceptors() {
    this.detachDebugInterceptors()

    const connections = [
      { name: 'Chat', conn: this.chatConnection },
      { name: 'Notifications', conn: this.notificationConnection },
      { name: 'Flow', conn: this.flowConnection },
    ]

    for (const { name, conn } of connections) {
      if (!conn) continue
      // Patch the internal _invokeClientMethod to intercept all events
      const original = (conn as any)._invokeClientMethod
      if (typeof original === 'function') {
        const patched = function (this: any, methodName: string, args: any[]) {
          const safeArgs = args || []
          console.groupCollapsed(
            `%c[SignalR ${name}]%c ${methodName}%c (${safeArgs.length} arg${safeArgs.length !== 1 ? 's' : ''})`,
            'color: #2196f3; font-weight: bold',
            'color: #ff9800; font-weight: bold',
            'color: #999; font-weight: normal'
          )
          safeArgs.forEach((arg: any, i: number) => {
            try {
              console.log(`%cArg ${i}:%c ${JSON.stringify(arg, null, 2)}`, 'color: #9c27b0; font-weight: bold', 'color: inherit')
            } catch {
              console.log(`Arg ${i}:`, arg)
            }
          })
          if (safeArgs.length > 0) {
            console.log('%cRaw:', 'color: #607d8b; font-weight: bold', ...safeArgs)
          }
          console.groupEnd()
          return original.call(this, methodName, args)
        };
        (conn as any)._invokeClientMethod = patched
        this.debugCleanups.push(() => {
          (conn as any)._invokeClientMethod = original
        })
      }
    }
  }

  detachDebugInterceptors() {
    this.debugCleanups.forEach(fn => fn())
    this.debugCleanups = []
  }
}

export const signalRService = new SignalRService()

// Expose global toggle: signalRDebug(true) / signalRDebug(false) / signalRDebug()
;(window as any).signalRDebug = (enabled?: boolean) => {
  if (enabled === undefined) {
    const state = signalRService.isDebugEnabled()
    console.log(`SignalR debug is ${state ? 'ON' : 'OFF'}. Usage: signalRDebug(true) or signalRDebug(false)`)
    return state
  }
  if (enabled) {
    signalRService.enableDebug()
  } else {
    signalRService.disableDebug()
  }
}
